// ---------------------------------------------------------------------------
// `bgSource` — what a saved scene records about WHICH IMAGE its background is,
// on top of `bgOption`, which only records the POSITION.
//
// Position stopped being enough the moment an admin could reorder the gallery.
// A scene saved with `bgOption: "3"` while the author held a draft arrangement
// means "the third tile I could see"; anybody else opening `/scene/:id` — a
// route that deliberately stays on the legacy background path — resolves the
// third tile of THEIR list and gets a different image. `bgSource` pins the
// image, and the loader prefers it over the position whenever it is present.
//
// Both shapes carry the url. A catalog background that came from a promoted
// upload has no legacy tile to fall back on, so the url is the only thing that
// can render it on the saved-scene path: the scene pins it (pinnedSceneBg).
// ---------------------------------------------------------------------------

export type SceneBgSource =
  | {kind: "catalog"; variation: number; url: string}
  | {kind: "image"; url: string};

/** The subset of a resolved list entry this module needs. */
interface EntryLike {
  /** Identity: the value `bgOption` holds (bgIdentity.ts). */
  variation: number;
  /** The catalog doc behind the entry, or null for a draft upload. */
  sourceVariation: number | null;
  imageUrl: string;
}

/**
 * What to record for the background at `bgOption`, or null when there is
 * nothing to pin — a non-numeric option ("custom", "generative"), or a
 * variation with no entry behind it.
 *
 * `pinnedUrl` is the image a saved link pins for `bgOption` now
 * (`activePinnedBgUrl`). The banners show it, so it is what a save records:
 * also when the gallery shows another image for the number, or lists the
 * number no more (a deleted background, kept by the link).
 */
export function buildSceneBgSource(
  bgOption: string | number | null | undefined,
  entries: EntryLike[] | null | undefined,
  pinnedUrl: string | null = null
): SceneBgSource | null {
  const variation = Number(bgOption);
  if (!Number.isFinite(variation) || !entries) return null;
  if (pinnedUrl && Number.isInteger(variation) && variation > 0) {
    return {kind: "catalog", variation, url: pinnedUrl};
  }
  const entry = entries.find((candidate) => candidate.variation === variation);
  if (!entry?.imageUrl) return null;
  return entry.sourceVariation === null
    ? {kind: "image", url: entry.imageUrl}
    : {kind: "catalog", variation: entry.sourceVariation, url: entry.imageUrl};
}

/**
 * How a loaded scene's background must be applied.
 *
 *   * `{bgOption}` — a numbered gallery background: every catalog source, also
 *     a promoted upload (variation 1000 and up), which has no tile folder. Its
 *     image is the url the scene pins (pinnedSceneBg), which wins over the
 *     tiles and the catalog. It must stay numbered: a banner shows its own
 *     ready cut (customBgs) only over a numbered background (templateOwnImage).
 *     As "custom", WebStorm's Releases emails on upload 1013 exported the
 *     whole upload instead of their Figma cuts (2026-11-03).
 *   * `{bgOption: "custom", customBg}` — render the url directly. Used for an
 *     image source: a draft upload with no catalog doc.
 *
 * Returns null when the scene has no `bgSource`, so a scene saved before the
 * field existed keeps its exact previous behaviour.
 */
export function resolveSceneBgSource(
  bgSource: SceneBgSource | null | undefined
): {bgOption: string; customBg?: string} | null {
  if (!bgSource || typeof bgSource !== "object") return null;
  if (bgSource.kind === "catalog") {
    return Number.isInteger(bgSource.variation) && bgSource.variation > 0
      ? {bgOption: String(bgSource.variation)}
      : null;
  }
  if (bgSource.kind === "image") {
    return bgSource.url ? {bgOption: "custom", customBg: bgSource.url} : null;
  }
  return null;
}

/**
 * Rewrite a loaded scene so its background comes from `bgSource`. Returns the
 * same object when there is nothing to apply, so the common path allocates
 * nothing and every pre-`bgSource` scene is untouched.
 */
export function applySceneBgSource<Scene extends Record<string, unknown>>(
  scene: Scene | null | undefined
): Scene | null | undefined {
  if (!scene) return scene;
  const resolved = resolveSceneBgSource(scene.bgSource as SceneBgSource | undefined);
  if (!resolved) return scene;
  return {
    ...scene,
    bgOption: resolved.bgOption,
    ...(resolved.customBg !== undefined ? {customBg: resolved.customBg} : {}),
  };
}

/** The exact image a loaded scene pins for its numbered background. */
export interface PinnedSceneBg {
  /** The scene the pin came from. The pin applies only on its own route. */
  sceneId: string;
  /** The catalog variation the scene saved; the pin applies only while it is selected. */
  variation: number;
  /** The image the author saw when they saved the scene. */
  url: string;
  /** The product the url is for. */
  product: string;
}

/**
 * The image a saved scene pins for a numbered catalog background, or null.
 *
 * A numbered background still resolves by variation on `/scene/:id`, and that
 * path reads the legacy Kraken tile first. The tiles are no longer maintained:
 * a variation whose catalog image was replaced still has its old tile, so the
 * saved scene, and every server export of it, showed the old picture. The
 * pinned url is what the author saw, so it wins over the tile.
 *
 * Null for a scene without `bgSource` (saved before the field existed: keeps
 * the tile path), for an image source (it renders the url as `customBg`), and
 * for the ALL-products view, where each banner shows a different product's
 * image.
 */
export function pinnedSceneBg(
  sceneId: string,
  scene: Record<string, unknown> | null | undefined
): PinnedSceneBg | null {
  const source = scene?.bgSource as SceneBgSource | undefined;
  if (!source || typeof source !== "object" || source.kind !== "catalog") return null;
  const resolved = resolveSceneBgSource(source);
  if (!resolved || resolved.bgOption === "custom") return null;
  if (typeof source.url !== "string" || source.url.length === 0) return null;
  const product = (scene?.product as {value?: unknown} | undefined)?.value;
  if (typeof product !== "string" || product.length === 0 || product === "ALL") return null;
  return {sceneId, variation: source.variation, url: source.url, product};
}

/**
 * The pinned url that applies to the banner being drawn, or null. The pin is
 * bound to its scene, to the variation it was saved with and to its product:
 * after the user picks another background, or on a child banner of another
 * product, the normal resolution applies again.
 */
export function activePinnedBgUrl(
  pin: PinnedSceneBg | null | undefined,
  {
    sceneId,
    bgOption,
    productValue,
  }: {
    sceneId: string | null | undefined;
    bgOption: string | number | null | undefined;
    productValue: string | null | undefined;
  }
): string | null {
  if (!pin || !sceneId || pin.sceneId !== sceneId) return null;
  if (String(pin.variation) !== String(bgOption)) return null;
  if (pin.product !== productValue) return null;
  return pin.url;
}
